import type {FormControl, WithoutBlocker} from 'platform/components';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {useWatch, type UseFormReturn} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CheckoutProfileTabSchema,
  type CheckoutProfile,
  type CheckoutProfileWrite,
  type CheckoutProfileError,
  type SaleRegime,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {queryParams} from '@dms/shared';

import {useQueryState} from 'shared/web';

import type {CheckoutProfileIssue} from '../../../utils/getCheckoutProfileIssueMessage';
import type {CheckoutProfileFormValues} from '../types/CheckoutProfileFormValuesSchema';
import {getCheckoutProfileConfigurationStatus} from '../utils/getCheckoutProfileConfigurationStatus';
import {getCheckoutProfileFeedbackSnapshot} from '../utils/getCheckoutProfileFeedbackSnapshot';
import {getCheckoutProfileLocalIssueField} from '../utils/getCheckoutProfileLocalIssueField';
import {getCheckoutProfileServerIssues} from '../utils/getCheckoutProfileServerIssues';

export interface CheckoutProfileServerResponse {
  errors: CheckoutProfileError[];
  values: CheckoutProfileWrite;
  // Computed once on refusal, so the draft is compared without re-parsing the submitted values.
  snapshot: string | undefined;
  tab: string;
}

export interface CheckoutProfileFormStateOptions {
  control: FormControl<CheckoutProfileFormValues>;
  formApi: UseFormReturn<CheckoutProfileFormValues>;
  profile?: CheckoutProfile;
  withoutBlocker: WithoutBlocker;
  serverResponse?: CheckoutProfileServerResponse;
}

export function useCheckoutProfileFormState(props: CheckoutProfileFormStateOptions) {
  const [, setTabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const [documentRegime, setDocumentRegime] = useState<SaleRegime>('DOMESTIC');
  const [navigation, setNavigation] = useState<{issue: CheckoutProfileIssue; sequence: number}>();
  const [
    name,
    branchId,
    typeOfSale,
    isActive,
    saleRegimes,
    foreignCurrency,
    payments,
    deposit,
    documentTypes,
    documents,
  ] = useWatch({
    control: props.control,
    name: [
      'name',
      'branchId',
      'typeOfSale',
      'isActive',
      'saleRegimes',
      'foreignCurrency',
      'payments',
      'deposit',
      'documentTypes',
      'documents',
    ],
  });

  const values: CheckoutProfileFormValues = {
    name,
    branchId,
    typeOfSale,
    isActive,
    saleRegimes,
    foreignCurrency,
    payments,
    deposit,
    documentTypes,
    documents,
  };

  const serverSnapshot = props.serverResponse?.snapshot;
  const responseIsCurrent =
    isNotNil(serverSnapshot) && getCheckoutProfileFeedbackSnapshot(values) === serverSnapshot;

  const serverIssues = getCheckoutProfileServerIssues({
    values,
    missing: props.formApi.formState.isDirty ? [] : props.profile?.missing,
    errors: responseIsCurrent ? props.serverResponse?.errors : [],
    submittedValues: props.serverResponse?.values,
  });

  const fieldErrors = Object.fromEntries(
    serverIssues
      .filter((issue) => issue.severity === 'error' && isNotNilOrEmpty(issue.field))
      .map((issue) => [issue.field, issue.message])
  );

  const status = getCheckoutProfileConfigurationStatus(
    values,
    props.formApi.formState.errors,
    serverIssues,
    isNotNil(props.profile) && !props.formApi.formState.isDirty
  );

  const issues = [...serverIssues];
  for (const tab of CheckoutProfileTabSchema.options) {
    if (status.tabs[tab] !== 'success' && !issues.some((issue) => issue.tab === tab)) {
      issues.push({
        code: `LOCAL_${tab}`,
        severity: status.tabs[tab] === 'error' ? 'error' : 'warning',
        tab,
        field: getCheckoutProfileLocalIssueField(tab, values),
        message:
          status.tabs[tab] === 'error'
            ? i18n.t('entity.checkout.validation.invalidConfiguration')
            : i18n.t('entity.checkout.validation.requiredConfiguration'),
      });
    }
  }

  const isDepositAllowed = deposit?.isAllowed;
  const isDocumentRegimeVisible = match(documentRegime)
    .with('DOMESTIC', () => true)
    .with('IN_EUROPE', () => saleRegimes?.inEurope === true)
    .with('OUT_OF_EUROPE', () => saleRegimes?.outOfEurope === true)
    .exhaustive();

  const activeDocumentRegime = isDocumentRegimeVisible ? documentRegime : 'DOMESTIC';
  const getNavigationTarget = (issue: CheckoutProfileIssue) =>
    issue.code === 'PROFILE_INCOMPLETE'
      ? issues.find(
          (candidate) =>
            candidate.code !== 'PROFILE_INCOMPLETE' &&
            isNotNil(candidate.tab) &&
            (isNotNilOrEmpty(candidate.field) || isNotNil(candidate.documentType))
        )
      : issue;

  const handleSelectIssue = (issue: CheckoutProfileIssue) => {
    const targetTab = issue.tab?.toLowerCase();

    if (isNil(targetTab)) {
      return;
    }

    if (isNotNil(issue.saleRegime)) {
      setDocumentRegime(issue.saleRegime);
    }

    setNavigation((current) => ({issue, sequence: (current?.sequence ?? 0) + 1}));
    props.withoutBlocker(() => setTabId(targetTab));
  };

  return {
    values,
    status,
    issues,
    fieldErrors,
    typeOfSale,
    isDepositAllowed,
    activeDocumentRegime,
    setDocumentRegime,
    navigation,
    setNavigation,
    setTabId,
    getNavigationTarget,
    handleSelectIssue,
  };
}
